import { useCallback, useEffect, useRef } from 'react';
import { Api } from '../application/panelApi';
import { applyClusterLivePatch, type Cluster } from '../features/clusters';
import { useApp } from '../store/AppContext';
import { useLogWs, useLogWsMessage } from './LogWsProvider';

type Options = {

  onTick?: () => Promise<unknown> | unknown;
};










export function useClusterLive(opts: Options = {}): { connected: boolean; refresh: () => Promise<void> } {
  const { clusters, controlMode, setClusters, refreshClusters } = useApp();
  const control = controlMode === 'control';
  const { connected } = useLogWs({ enabled: control });

  const clustersRef = useRef<Cluster[]>(clusters as Cluster[]);
  clustersRef.current = clusters as Cluster[];
  const connectedRef = useRef(connected);
  connectedRef.current = connected;
  const onTickRef = useRef(opts.onTick);
  onTickRef.current = opts.onTick;
  const busyRef = useRef(false);

  useLogWsMessage(
    'cluster_live',
    (msg) => {
      const list = Array.isArray(msg.clusters) ? (msg.clusters as Array<Partial<Cluster>>) : [];
      if (!list.length) return;
      const next = applyClusterLivePatch(clustersRef.current, list);
      if (next) setClusters(next);
    },
    control,
  );

  const refresh = useCallback(async () => {
    if (!control || busyRef.current) return;
    busyRef.current = true;
    try {
      const deploying = clustersRef.current.some((c) => c.status === 'deploying');
      if (deploying || !connectedRef.current) {
        await Api.clusters.pingAll().catch(() => undefined);
      }
      await refreshClusters().catch(() => undefined);
      await onTickRef.current?.();
    } finally {
      busyRef.current = false;
    }
  }, [control, refreshClusters]);

  const deploying = (clusters as Cluster[]).some((c) => c.status === 'deploying');
  const wantMs = deploying ? 2500 : connected ? 30_000 : 8_000;

  useEffect(() => {
    if (!control) return undefined;
    const timer = window.setInterval(() => void refresh(), wantMs);
    return () => window.clearInterval(timer);
  }, [control, wantMs, refresh]);

  useEffect(() => {
    if (!control) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [control, refresh]);

  return { connected, refresh };
}
